import { Category, CourseData, RequiredExcept, SavedCourse, STEP_TYPE } from "@vixi/types";
import { assert } from "../assert";
import findCategory from "../categories/findCategory";

function savedStepToStep(savedStep: SavedCourse.Step): CourseData.Step {
  const step: CourseData.Step = {
    id: savedStep.id ?? savedStep.stepOrder,
    order: savedStep.stepOrder,
    type: savedStep.lessonStepType,
  };

  let options: string[];

  switch (savedStep.lessonStepType) {
    case STEP_TYPE.CONVERSATION:
      assert(!!savedStep.stepMessages?.length, "Conversation step must have messages");
      step.messages = savedStep.stepMessages.map(({ authorMessage, learnerMessage, order }) => ({
        authorMessage,
        learnerMessage,
        order,
      }));
      break;

    case STEP_TYPE.MCQ:
      assert(!!savedStep.stepMultipleChoice, "MCQ step must have multiple choice question");
      const mc = savedStep.stepMultipleChoice;
      options = Object.keys(mc)
        .sort()
        .filter((key) => key.startsWith("option"))
        .map((key) => mc[key as keyof SavedCourse.McQuestion]);

      step.multipleChoice = {
        question: mc.question,
        options,
        correctAnswer: options.indexOf(mc.correctAnswer),
      };
      break;

    case STEP_TYPE.FITB:
      assert(!!savedStep.stepFillInTheBlank, "FITB step must have fill in the blank question");
      const fitb = savedStep.stepFillInTheBlank;
      options = Object.keys(fitb)
        .sort()
        .filter((key) => key.startsWith("option"))
        .map((key) => fitb[key as keyof SavedCourse.FitbQuestion]);

      step.fillInTheBlank = {
        question: savedStep.stepFillInTheBlank.question,
        options: options,
        correctAnswer: options.indexOf(savedStep.stepFillInTheBlank.correctAnswer),
      };
      break;

    case STEP_TYPE.MATCHING:
      assert(!!savedStep.stepMatching, "Matching step must have matching question");
      const matching = savedStep.stepMatching;

      options = Object.keys(matching)
        .sort()
        .filter((key) => key.startsWith("option"))
        .map((key) => matching[key as `option${number}`]);

      let answers = Object.keys(matching)
        .sort()
        .filter((key) => key.startsWith("answer"))
        .map((key) => matching[key as `answer${number}`]);

      step.matching = {
        question: savedStep.stepMatching.question,
        answers: options.map((option, index) => ({
          question: option,
          answer: answers[index],
        })),
      };
      break;
    default:
      assert(false, "Invalid step type");
  }

  return step;
}

export const savedCourseToCourse = (
  { units, ...savedCourse }: SavedCourse.Course,
  categories?: Category[],
): RequiredExcept<CourseData.Course, "category" | "subcategory"> => {
  const res: CourseData.Course = { ...savedCourse };

  res.chapters = units
    ?.sort((a, b) => a.unitOrder - b.unitOrder)
    .map(({ lessons, unitOrder, ...unit }) => {
      return {
        ...unit,
        id: unit.id ?? unitOrder,
        order: unitOrder,
        lessons: lessons
          .sort((a, b) => a.lessonOrder - b.lessonOrder)
          .map(({ lessonOrder, lessonSteps, selectedTitle, ...lesson }) => {
            const titles = Object.keys(lesson)
              .sort()
              .filter((key) => key.startsWith("title"))
              .map((key) => lesson[key as `title${number}`]);

            return {
              ...lesson,
              id: lesson.id ?? lessonOrder,
              titles,
              order: lessonOrder,
              selectedTitle: titles.indexOf(selectedTitle),
              steps: lessonSteps.sort((a, b) => a.stepOrder - b.stepOrder).map(savedStepToStep),
            };
          }),
      };
    });

  if (categories) {
    const [category, subcategory] = findCategory(savedCourse.categoryId, categories);
    res.category = category ?? subcategory;
  }

  return res as RequiredExcept<CourseData.Course, "category" | "subcategory">;
};
